import React, { useState, useEffect, useCallback } from "react";
import { api, MXN, formatDateMX, fileUrl } from "../../lib/apiClient";
import { ORDER_STATUSES, PAYMENT_STATUSES, PAYMENT_METHODS, statusMeta } from "../../lib/constants";
import { adminOrderWhatsappMessage, openWhatsappBusiness } from "../../lib/whatsapp";
import { toast } from "sonner";
import { X, MessageCircle, Save, Plus } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

export const OrderDrawer = ({ orderId, settings, onClose, onUpdated }) => {
  const [o, setO] = useState(null);
  const [notes, setNotes] = useState("");
  const [finalPrice, setFinalPrice] = useState("");
  const [pay, setPay] = useState({ amount: "", method: "Efectivo", notes: "" });

  const load = useCallback(() => api.get(`/orders/${orderId}`).then((r) => { setO(r.data); setNotes(r.data.internal_notes || ""); setFinalPrice(r.data.final_price ?? ""); }), [orderId]);
  useEffect(() => { if (orderId) load(); }, [orderId, load]);
  const refreshIfCurrentOrderChanged = useCallback((event) => {
    if (!event?.payload?.id || event.payload.id === orderId) load();
  }, [load, orderId]);
  useAdminRealtime(refreshIfCurrentOrderChanged, ["orders"]);

  if (!orderId) return null;

  const patch = async (body, msg) => {
    await api.patch(`/orders/${orderId}`, body);
    await load(); onUpdated && onUpdated();
    if (msg) toast.success(msg);
  };

  const addPayment = async () => {
    if (!pay.amount) { toast.error("Ingresa el monto."); return; }
    await api.post(`/orders/${orderId}/payments`, { amount: parseFloat(pay.amount), method: pay.method, notes: pay.notes });
    setPay({ amount: "", method: "Efectivo", notes: "" });
    await load(); onUpdated && onUpdated();
    toast.success("Pago registrado 💛");
  };

  const cz = o?.customization || {};
  const paid = (o?.payments || []).reduce((a, p) => a + p.amount, 0);
  const saldo = Number(o?.final_price || 0) - paid;
  const contactCustomer = () => {
    if (!o?.customer_phone) {
      toast.error("Este pedido no tiene WhatsApp del cliente.");
      return;
    }
    openWhatsappBusiness(o.customer_phone, adminOrderWhatsappMessage(o));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-drawer">
      <div className="absolute inset-0 bg-vinoDark/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-crema h-full overflow-y-auto shadow-2xl overflow-x-hidden">
        {!o ? <div className="p-8 text-cafe/50">Cargando…</div> : (
          <div className="p-4 sm:p-6 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="min-w-0">
                <p className="text-xs text-cafe/50">Folio</p>
                <p className="font-serif text-2xl text-vino truncate">{o.folio}</p>
              </div>
              <button onClick={onClose} data-testid="drawer-close" className="text-vino p-2 hover:bg-beige rounded-full shrink-0"><X /></button>
            </div>

            {/* Status */}
            <div className="flex flex-wrap gap-2 mb-4">
              {ORDER_STATUSES.map((st) => (
                <button key={st.value} onClick={() => patch({ status: st.value }, "Estado actualizado")} data-testid={`status-${st.value}`}
                  className={`text-xs px-3 py-2 rounded-full border-2 transition-all ${o.status === st.value ? "text-white border-transparent" : "border-beige text-cafe/60"}`}
                  style={o.status === st.value ? { background: st.color } : {}}>{st.label}</button>
              ))}
            </div>

            <Section title="Cliente">
              <Info l="Nombre" v={o.customer_name} />
              <Info l="Teléfono" v={o.customer_phone} />
              {o.customer_email && <Info l="Correo" v={o.customer_email} />}
            </Section>

            <Section title="Pedido">
              <Info l="Producto" v={`${o.product_name} × ${o.quantity}`} />
              {cz.tamano && <Info l="Tamaño" v={cz.tamano} />}
              {cz.sabor && <Info l="Sabor" v={cz.sabor} />}
              {cz.relleno && <Info l="Relleno" v={cz.relleno} />}
              {cz.decoracion && <Info l="Decoración" v={cz.decoracion} />}
              {cz.extras?.length > 0 && <Info l="Extras" v={cz.extras.join(", ")} />}
              {cz.mensaje && <Info l="Mensaje" v={cz.mensaje} />}
              {cz.cart_items?.length > 0 && (
                <div>
                  <p className="text-xs text-cafe/50 mb-2">Productos del carrito</p>
                  <div className="space-y-2">
                    {cz.cart_items.map((item, idx) => (
                      <div key={idx} className="bg-crema border border-beige rounded-2xl p-3">
                        <p className="text-sm font-medium text-vino">{item.product_name} × {item.quantity}</p>
                        {item.line_total ? <p className="text-xs text-cafe/50">${item.line_total} MXN</p> : <p className="text-xs text-cafe/50">Por cotización</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {cz.comentarios && <Info l="Comentarios" v={cz.comentarios} />}
            </Section>

            {o.reference_images?.length > 0 && (
              <Section title="Imágenes de referencia">
                <div className="flex flex-wrap gap-2">
                  {o.reference_images.map((p) => <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} className="w-20 h-20 rounded-xl object-cover border border-beige" alt="ref" /></a>)}
                </div>
              </Section>
            )}

            <Section title="Entrega">
              <Info l="Fecha" v={formatDateMX(o.delivery_date)} />
              <Info l="Hora" v={o.delivery_time || "-"} />
              <Info l="Método" v={o.delivery_method} />
              {o.delivery_method === "envio" && o.address?.calle && <Info l="Dirección" v={`${o.address.calle}, ${o.address.colonia || ""} ${o.address.referencias || ""}`} />}
              {o.delivery_method === "evento" && o.event?.direccion && <Info l="Evento" v={`${o.event.direccion} · ${o.event.hora || ""}`} />}
            </Section>

            {/* Pricing & payments */}
            <Section title="Pagos">
              <div className="grid grid-cols-[1fr_auto] gap-2 items-end mb-3">
                <div className="flex-1">
                  <label className="text-xs text-cafe/60">Precio final (MXN)</label>
                  <input data-testid="final-price" type="number" value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)}
                    className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 text-cafe focus:outline-none focus:border-vino" />
                </div>
                <button onClick={() => patch({ final_price: parseFloat(finalPrice) || 0 }, "Precio guardado")} data-testid="save-price" className="bg-vino text-crema rounded-xl px-4 py-2 text-sm flex items-center gap-1"><Save className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 min-[360px]:grid-cols-3 gap-2 text-center text-sm mb-3">
                <div className="bg-blancoCalido rounded-xl p-2 border border-beige"><p className="text-xs text-cafe/50">Total</p><p className="font-medium text-vino">{MXN(o.final_price)}</p></div>
                <div className="bg-blancoCalido rounded-xl p-2 border border-beige"><p className="text-xs text-cafe/50">Pagado</p><p className="font-medium text-salvia">{MXN(paid)}</p></div>
                <div className="bg-blancoCalido rounded-xl p-2 border border-beige"><p className="text-xs text-cafe/50">Saldo</p><p className="font-medium text-dorado">{MXN(saldo)}</p></div>
              </div>
              <span className="inline-block text-xs px-3 py-1 rounded-full bg-beige text-vino mb-3">{PAYMENT_STATUSES.find((p) => p.value === o.payment_status)?.label}</span>
              {(o.payments || []).map((p, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b border-beige/60"><span className="text-cafe/70">{p.date} · {p.method}</span><span className="text-cafe">{MXN(p.amount)}</span></div>
              ))}
              <div className="mt-3 grid grid-cols-1 min-[380px]:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <input data-testid="pay-amount" type="number" placeholder="Monto" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino" />
                <select data-testid="pay-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino">
                  {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
                </select>
                <button onClick={addPayment} data-testid="add-payment" className="w-full min-[380px]:w-auto justify-center bg-salvia text-white rounded-xl px-4 py-2 text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Pago</button>
              </div>
            </Section>

            {/* Internal notes */}
            <Section title="Notas internas">
              <textarea data-testid="internal-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full bg-blancoCalido border-2 border-beige rounded-xl px-3 py-2 text-cafe focus:outline-none focus:border-vino" />
              <button onClick={() => patch({ internal_notes: notes }, "Notas guardadas")} data-testid="save-notes" className="mt-2 bg-vino text-crema rounded-full px-5 py-2 text-sm">Guardar notas</button>
            </Section>

            {o.history?.length > 0 && (
              <Section title="Historial">
                {o.history.map((h, i) => <p key={i} className="text-xs text-cafe/50">{h.text} · {(h.at || "").slice(0, 16).replace("T", " ")}</p>)}
              </Section>
            )}

            <button type="button" onClick={contactCustomer} data-testid="drawer-whatsapp" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full py-3 font-medium">
              <MessageCircle className="w-5 h-5" /> Escribir por WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-blancoCalido border border-beige rounded-[20px] p-4 mb-3">
    <p className="font-medium text-vino text-sm mb-2">{title}</p>
    <div className="space-y-1">{children}</div>
  </div>
);
const Info = ({ l, v }) => <div className="flex flex-col min-[380px]:flex-row min-[380px]:justify-between gap-0.5 min-[380px]:gap-3 text-sm min-w-0"><span className="text-cafe/60 shrink-0">{l}</span><span className="text-cafe min-[380px]:text-right break-words min-w-0">{v}</span></div>;
